import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label for the section that failed (e.g. "MCP") */
  label?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Catches render errors AND lazy chunk load failures (React.lazy rejects are
 * surfaced here once the Suspense boundary throws). Without this, a single
 * transient failure (e.g. gateway restart mid-chunk-download) unmounts the
 * whole app -> white screen that persists until a hard refresh, because
 * React.lazy caches the rejected import promise.
 *
 * Retry re-mounts the boundary children, which re-triggers the lazy import.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(err: unknown): State {
    const msg = err instanceof Error ? err.message : String(err);
    const isChunk = /loading chunk|import\(\)|dynamically imported|failed to fetch/i.test(msg);
    return {
      hasError: true,
      message: isChunk ? 'The page failed to load — the module download was interrupted.' : msg,
    };
  }

  componentDidCatch(err: unknown) {
    console.error('[ErrorBoundary]', err);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-10 max-w-md text-center dark:bg-slate-900 dark:border-slate-700">
            <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-4 text-2xl dark:bg-amber-500/10">!</div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100">
              {this.props.label ? `${this.props.label} failed to load` : 'Something went wrong'}
            </h2>
            <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">{this.state.message}</p>
            <button
              onClick={this.handleRetry}
              className="mt-5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
